import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "./primitives.js";
import { Icon } from "./icons.js";

interface Props {
  /** What failed, in the words the panel uses for itself. */
  area: string;
  /** Grid area to occupy when the fallback replaces a whole region. */
  slot?: "toolbar" | "left" | "canvas" | "right";
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Keeps one part of the designer's failure to itself.
 *
 * React unmounts the whole tree from the nearest boundary up, so without one a
 * single bad render — a panel reading a field that a host's `update()` has not
 * filled in yet, a custom block throwing in its inspector — replaces the entire
 * editor with a blank element, taking the user's unsaved template off the
 * screen with it. Each region gets its own boundary instead: the rest of the
 * designer keeps working, the document is still there, and the part that broke
 * offers to try again rather than needing the page reloaded.
 */
export class Boundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hosts have their own reporting; ours is a console message that names the
    // area, because "Cannot read properties of undefined" on its own does not.
    console.error(`[broadpaper] ${this.props.area} failed to render`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="bpe-boundary" role="alert" style={this.props.slot ? { gridArea: this.props.slot } : undefined}>
        <Icon name="alert-triangle" size={16} />
        <div className="bpe-boundary__body">
          <div className="bpe-boundary__title">{this.props.area} stopped</div>
          <div className="bpe-boundary__detail">{error.message || "Something went wrong."} The rest of the designer, and your document, are unaffected.</div>
        </div>
        <Button size="sm" icon="rotate-ccw" onClick={() => this.setState({ error: null })}>Try again</Button>
      </div>
    );
  }
}
